/**
 * The account piece's standard look (bookings.html's header controls): Sign in
 * for a visitor; signed in, the round Messages button with its unread dot and
 * the client's initials opening the account menu. Draws the behavior half's
 * state and calls its actions; holds no rule.
 */
import { useLayoutEffect, useRef } from 'react';

import { partsOf, pieceRoot } from '../../contract/runtime';
import { onLink } from '../../ui/onLink';
import { Icon } from '../../ui/Icon';
import css from './Account.module.css';
import { contract } from './contract';
import type { AccountRuntime } from './useAccount';

const part = partsOf(contract);

/** The space kept between the open menu and the window's edge. */
const EDGE = 8;

/**
 * D4: the open menu keeps inside the window on its own. It hangs from the initials' right edge, and her bag and
 * menu buttons may sit to the right of them, so on a narrow screen it is moved back in by the overflow.
 */
function useMenuOnScreen(open: boolean) {
  const menu = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const el = menu.current;
    if (!open || !el) return;
    const place = () => {
      el.style.translate = '';
      const r = el.getBoundingClientRect();
      const width = document.documentElement.clientWidth;
      const shift = r.left < EDGE ? EDGE - r.left : r.right > width - EDGE ? width - EDGE - r.right : 0;
      if (shift) el.style.translate = `${shift}px 0`;
    };
    place();
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, [open]);
  return menu;
}

export function AccountView({ state, actions }: AccountRuntime) {
  const menu = useMenuOnScreen(state.menuOpen);
  return (
    <div className={css.account} {...pieceRoot(contract)}>
      {state.session !== 'signedIn' && (
        <button
          type="button"
          className={css.signIn}
          aria-label="Sign in with Telegram"
          disabled={!state.signInReady || state.signIn === 'pending'}
          onClick={actions.signIn}
          {...part('signIn')}
        >
          Sign in
        </button>
      )}
      {state.client && (
        <button
          type="button"
          className={css.messages}
          aria-label={state.unread > 0 ? `Messages, ${state.unread} new` : 'Messages'}
          aria-expanded={state.messagesOpen}
          aria-controls="web-messages"
          onClick={actions.toggleMessages}
          {...part('messages')}
        >
          <Icon name="msg" />
          {state.unread > 0 && <span className={css.dot} aria-hidden="true" {...part('messagesBadge')} />}
        </button>
      )}
      {state.client && (
        <div
          className={css.who}
          onBlur={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget as Node | null)) actions.toggleMenu(false);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') actions.toggleMenu(false);
          }}
        >
          <button
            type="button"
            className={css.avatar}
            aria-haspopup="menu"
            aria-expanded={state.menuOpen}
            aria-label={`${state.client.name}, your account`}
            onClick={() => actions.toggleMenu()}
            {...part('avatar', state.menuOpen && 'open')}
          >
            {state.client.initials}
          </button>
          {state.menuOpen && (
            <div className={css.menu} ref={menu} {...part('menu')}>
              <p className={css.menuHead} {...part('menuHead')}>
                <b className={css.menuName} {...part('menuName')}>
                  {state.client.name}
                </b>
                <span className={css.menuNote} {...part('menuNote')}>
                  Signed in with Telegram
                </span>
              </p>
              <div role="menu" aria-label="Your account">
                <a
                  role="menuitem"
                  className={css.menuItem}
                  href="/account/bookings"
                  onClick={onLink(() => actions.openAccount('bookings'))}
                  {...part('menuBookings')}
                >
                  Bookings
                </a>
                <button type="button" role="menuitem" className={css.menuItem} onClick={actions.toggleMessages} {...part('menuMessages')}>
                  Messages
                </button>
                <a
                  role="menuitem"
                  className={css.menuItem}
                  href="/account/profile"
                  onClick={onLink(() => actions.openAccount('profile'))}
                  {...part('menuProfile')}
                >
                  Profile
                </a>
                <button type="button" role="menuitem" className={css.menuItem} onClick={actions.signOut} {...part('signOut')}>
                  Sign out
                </button>
              </div>
              {state.signOutFailed && (
                <p className={css.menuError} role="alert" {...part('signOutError')}>
                  Sign out did not go through. Please try again.
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
